// Same-origin WebSocket uplink for streaming dictation.
//
// Capture never waits for the network: frames pushed while the socket is
// anything other than OPEN queue in memory (bounded to ~10 minutes) and
// drain in order on (re)connect. If this socket drops mid-take the
// browser keeps capturing, reconnects with backoff and re-sends 'start';
// the server then opens a fresh upstream session and the queued frames
// flow — worst case some text lands late and a partial fragment reaches
// the review queue, never the bin.

import type { StreamServerEvent } from '@shared/types.js';

export type SocketState = 'connecting' | 'open' | 'closed';

const QUEUE_MAX_FRAMES = 6000; // 10 min of 100 ms frames

export class StreamClient {
  private ws: WebSocket | null = null;
  private queue: ArrayBuffer[] = [];
  private sessionId: string | null = null;
  private closedByUs = false;
  private reconnectTimer: number | null = null;
  private attempts = 0;

  constructor(
    private handlers: {
      onEvent: (e: StreamServerEvent) => void;
      onSocketState?: (s: SocketState) => void;
    },
  ) {}

  /** Open the socket ahead of time so record-start has a warm pipe. */
  connect(): void {
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;
    this.closedByUs = false;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${location.host}/api/transcription/stream`;
    this.handlers.onSocketState?.('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.attempts = 0;
      this.handlers.onSocketState?.('open');
      if (this.sessionId) {
        ws.send(JSON.stringify({ type: 'start', sessionId: this.sessionId }));
        this.drain();
      }
    };
    ws.onmessage = (ev) => {
      try {
        this.handlers.onEvent(JSON.parse(String(ev.data)) as StreamServerEvent);
      } catch {
        /* ignore unparseable frames */
      }
    };
    ws.onclose = () => {
      this.ws = null;
      this.handlers.onSocketState?.('closed');
      if (!this.closedByUs) this.scheduleReconnect();
    };
    ws.onerror = () => {
      /* onclose follows and handles it */
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || this.closedByUs) return;
    const delay = Math.min(500 * 2 ** this.attempts, 8000);
    this.attempts += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /** Begin (or rebind) a take for the given capture session. */
  start(sessionId: string): void {
    this.sessionId = sessionId;
    this.connect();
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'start', sessionId }));
      this.drain();
    }
  }

  pushFrame(frame: ArrayBuffer): void {
    if (this.ws?.readyState === WebSocket.OPEN && this.queue.length === 0) {
      this.ws.send(frame);
      return;
    }
    this.queue.push(frame);
    if (this.queue.length > QUEUE_MAX_FRAMES) this.queue.shift();
    this.drain();
  }

  private drain(): void {
    while (this.queue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(this.queue.shift()!);
    }
  }

  /** Ask the server to finish the take (flush + summary event). */
  stop(): void {
    this.drain();
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'stop' }));
    }
    this.sessionId = null;
  }

  /** How many frames are still waiting for the socket. */
  pendingFrames(): number {
    return this.queue.length;
  }

  close(): void {
    this.closedByUs = true;
    this.sessionId = null;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
    this.queue = [];
  }
}
